import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useFirebaseAuth } from '../services/firebaseAuthService';
import { useAuth } from '../context/AuthContext';

export default function LoginSection() {
  const { user, loading, unverifiedUser } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [unverifiedPassword, setUnverifiedPassword] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const { signInWithEmail, signInWithGoogle, resendVerificationEmail } = useFirebaseAuth();
  
  // Check for unverified user from context
  useEffect(() => {
    if (unverifiedUser) {
      setUnverifiedEmail(unverifiedUser.email || '');
      setNeedsVerification(true);
    }
  }, [unverifiedUser]);

  // Handle auth redirects
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={location.state?.from || '/'} replace />;
  }

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      await signInWithEmail(email, password);
      // Success handled by auth redirect
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/email-not-verified') {
        setUnverifiedEmail(err.email || email);
        setUnverifiedPassword(password); // Store password for resend functionality
        setNeedsVerification(true);
      } else {
        setError('Failed to sign in. Please check your email and password.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await signInWithGoogle();
      // No navigate needed - will be handled by auth state change
    } catch (err) {
      console.error('Google login error:', err);
      setError('Failed to sign in with Google.');
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setIsSubmitting(true);
      await resendVerificationEmail(unverifiedEmail, unverifiedPassword);
      setVerificationSent(true);
      setTimeout(() => setVerificationSent(false), 5000); // Reset after 5 seconds
    } catch (err) {
      console.error("Resend verification error:", err);
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    setNeedsVerification(false);
    setUnverifiedEmail('');
    setUnverifiedPassword('');
  };

  // Show verification screen if needed
  if (needsVerification) {
    return (
      <section className="bg-white pt-4 pb-12 mt-25 md:pb-24">
        <div className="container mx-auto px-4">
          <div className="max-w-xl mx-auto text-center">
            <h3 className="text-4xl sm:text-5xl font-semibold mb-6">Email Verification Required</h3>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8 text-left rounded">
              <p className="text-lg mb-4">
                Your email <span className="font-semibold">{unverifiedEmail}</span> needs to be verified.
              </p>
              <p className="mb-4">Please check your inbox for a verification email or request a new one below.</p>
            </div>
            
            {error && <p className="text-red-500 mb-4">{error}</p>}
            
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button
                className="px-6 py-3 font-semibold text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition duration-200"
                onClick={handleResendVerification}
                disabled={isSubmitting || verificationSent}
              >
                {verificationSent ? "Verification Email Sent!" : "Send Verification Email"}
              </button>
              
              <button
                className="px-6 py-3 font-semibold text-neutral-600 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition duration-200"
                onClick={handleBackToLogin}
                disabled={isSubmitting}
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white pt-4 pb-12 mt-25 md:pb-24">
      <div className="container mx-auto px-4">
        <div className="max-w-xl mx-auto">
          <form onSubmit={handleSignIn}>
            <h3 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-semibold mb-16">
              Sign in
            </h3>
            {error && <p className="text-red-500 mb-4">{error}</p>}
            <button
              type="button"
              className="flex justify-center items-center w-full mb-6 p-4 font-semibold border border-neutral-900 bg-white hover:border-blue-500 hover:text-blue-500 rounded-lg transition duration-200"
              onClick={handleGoogleSignIn}
              disabled={isSubmitting}
            >
              <img className="mr-2 h-6" src="/assets/auth/google.svg" alt="Google logo" />
              <span>Continue with Google</span>
            </button>
            <p className="text-center text-neutral-600 font-medium mb-4">OR</p>
            <InputField
              label="Email address"
              id="email"
              type="email"
              placeholder="ex. d.duncan@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <InputField
              label="Password"
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <a className="flex justify-end text-neutral-600 font-medium mb-12 hover:text-neutral-800 transition duration-200" href="/forgot-password">
              Forgot my password
            </a>
            <button
              className="flex justify-center items-center w-full mb-6 p-5 font-semibold text-lg text-white bg-neutral-900 hover:bg-neutral-800 rounded-lg transition duration-200"
              type="submit"
              disabled={isSubmitting}
            >
              Sign in
            </button>
            <a className="inline-block text-neutral-600 text-sm font-medium hover:text-neutral-800 transition duration-200" href="/register">
              Don't have an account? Sign Up
            </a>
          </form>
        </div>
      </div>
    </section>
  );
}

function InputField({ label, id, type, placeholder, value, onChange }) {
  return (
    <div className="mb-6">
      <label className="block mb-3 text-neutral-600 font-medium" htmlFor={id}>
        {label}
      </label>
      <input
        className="w-full px-8 py-5 text-lg outline-none rounded-lg border border-neutral-100 placeholder-neutral-300 font-medium focus:ring-4 focus:ring-gray-600 transition duration-200"
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
      />
    </div>
  );
}